// Vendor Libs
import React from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash.isempty';
import isObject from 'lodash.isobject';
import map from 'lodash.map';
import union from 'lodash.union';

// Material UI
import {TreeItem, TreeView} from '@material-ui/lab';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ArrowRightIcon from '@material-ui/icons/ArrowRight';
import {InputLabel, Typography} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';

// Components
import TreeNode from './tree-node';

const useStyles = makeStyles(theme => ({
    clientTreeLabel: {
        padding: theme.spacing(2, 0),
    },
    label: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    labelText: {
        fontSize: '1rem',
        fontWeight: 'bold',
    },
}));

const RoleTree = ({editedRoleId, label, onChange, roleData, selectedRoles}) => {
    const classes = useStyles();
    const unique = arr => [...new Set(arr)];

    const remove = (arr, value) => arr.filter(item => item !== value);

    const getRootRoles = () => {
        let rootRoles = [];

        const traverse = (obj, root) => {
            if (Array.isArray(obj)) {
                if (isEmpty(root)) {
                    map(obj, node => rootRoles.push(node.roleId));
                }
                map(obj, node => traverse(node, root));
            } else if (isObject(obj)) {
                if (!isEmpty(root)) {
                    rootRoles = rootRoles.filter(item => item !== obj.roleId);
                }
                traverse(obj.childRoles, [obj.roleId]);
            }
        };

        traverse(roleData, []);

        return rootRoles;
    };

    const getAllRoles = roleIds => {
        const allRoles = [];
        const traverse = (obj, root) => {
            if (Array.isArray(obj)) {
                map(obj, node => traverse(node, root));
            } else if (isObject(obj)) {
                if (!isEmpty(root)) {
                    allRoles.push(obj.roleId);
                    traverse(obj.childRoles, [...root, obj.roleId]);
                } else {
                    allRoles.push(obj.roleId);
                    traverse(obj.childRoles, [obj.roleId]);
                }
            }
        };

        traverse(roleData.filter(role => roleIds.includes(role.roleId)));

        return unique(allRoles);
    };

    const getChildRoles = () => {
        const childRoles = {};
        const traverse = (obj, root) => {
            if (Array.isArray(obj)) {
                if (!isEmpty(root)) {
                    map(root, role => {
                        if (!(role in childRoles)) {
                            childRoles[role] = [];
                        }
                        map(obj, node => {
                            childRoles[role] = union(childRoles[role], [node.roleId]);
                            return traverse(node, root);
                        });
                    });
                } else {
                    map(obj, node => traverse(node, root));
                }
            } else if (isObject(obj)) {
                if (!isEmpty(root)) {
                    traverse(obj.childRoles, [...root, obj.roleId]);
                } else {
                    traverse(obj.childRoles, [obj.roleId]);
                }
            }
        };

        traverse(roleData, []);

        return childRoles;
    };

    const isChecked = roleId => selectedRoles.includes(roleId) || getAllRoles(selectedRoles).includes(roleId);

    /*
     * Bidirectional tree selection: checking a parent collapses its children into the parent
     * selection (parent implies children); unchecking a parent re-expands its direct children
     * as individual selections so users can still selectively deselect sub-trees.
     */
    const handleChange = event => {
        event.persist();

        const {target: {value, checked} = {}} = event;
        const currentRoleId = Number(value);
        const childRoles = getChildRoles(roleData);
        let newRoles = unique([...selectedRoles, currentRoleId]);
        const children = [];
        const removeRole = roleId => {
            if (childRoles[roleId].includes(currentRoleId)) {
                newRoles = [...newRoles, ...childRoles[roleId]];
                newRoles = remove(newRoles, roleId);
            }
        };

        if (checked) {
            // Remove children from list if parent is checked
            newRoles.forEach(roleId => {
                if (roleId in childRoles) {
                    children.push(...childRoles[roleId]);
                }
            });

            newRoles = newRoles.filter(roleId => !children.includes(roleId));
        } else {
            newRoles = [...selectedRoles];

            while (newRoles.find(roleId => childRoles[roleId].includes(currentRoleId))) {
                newRoles.forEach(removeRole);
            }

            newRoles = remove(newRoles, currentRoleId);
            newRoles = [...newRoles, ...childRoles[currentRoleId]];
        }

        onChange(unique(newRoles));
    };

    const isAncestor = ({roleId, childRoles = []}) => roleId === editedRoleId || childRoles.some(isAncestor);

    const renderTree = role => {
        const {roleId, childRoles, name} = role;

        return (
            <TreeNode
                checked={isChecked(roleId)}
                disabled={!!editedRoleId && isAncestor(role)}
                key={String(roleId)}
                label={`${name}`.toLowerCase()}
                nodeId={String(roleId)}
                value={String(roleId)}
                onChange={handleChange}
            >
                {childRoles && !isEmpty(childRoles) ? map(childRoles, child => renderTree(child)) : undefined}
            </TreeNode>
        );
    };

    const rootRoles = getRootRoles();

    const uniqueClients = [...new Set(roleData.map(role => role.client.clientId))];

    const getNodeIds = length =>
        [...new Set([...Array(length).keys(), ...roleData.map(role => role.roleId)])].map(String);

    return !isEmpty(roleData) ? (
        <>
            <InputLabel className={classes.label}>{label}</InputLabel>
            <TreeView
                defaultCollapseIcon={<ArrowDropDownIcon />}
                defaultEndIcon={<div style={{width: 24}} />}
                defaultExpandIcon={<ArrowRightIcon />}
                defaultExpanded={getNodeIds(rootRoles.length)}
                onNodeToggle={e => e.preventDefault()}
            >
                {uniqueClients.map((client, index) => (
                    <React.Fragment key={index}>
                        <TreeItem
                            label={
                                <div className={classes.clientTreeLabel}>
                                    <Typography className={classes.labelText} variant="body2">
                                        {client}
                                    </Typography>
                                </div>
                            }
                            nodeId={index.toString()}
                        >
                            {roleData
                                .filter(role => rootRoles.includes(role.roleId))
                                .filter(role => role.client.clientId === client)
                                .map(role => renderTree(role))}
                        </TreeItem>
                    </React.Fragment>
                ))}
            </TreeView>
        </>
    ) : null;
};

RoleTree.propTypes = {
    editedRoleId: PropTypes.number,
    label: PropTypes.string,
    onChange: PropTypes.func,
    roleData: PropTypes.array,
    selectedRoles: PropTypes.array,
};

export default RoleTree;
